import {
    Platform
} from "react-native";
import type {
    DropdownMenuTypeaheadConstantType,
    DropdownMenuShortcutConstantType,
    DropdownMenuShortcutResultType
} from "./type";
import type {
    KbdPlatform
} from "../kbd/type";
import {
    resolveKbdKey,
    parseKbdKeys
} from "../kbd/util";

export const DROPDOWN_MENU_ITEM_SELECTOR = "[role=\"menuitem\"], [role=\"menuitemcheckbox\"], [role=\"menuitemradio\"]";

export const DROPDOWN_MENU_CONTEXT_MENU_KEY = "ContextMenu";

export const DROPDOWN_MENU_ARROW_DOWN_KEY = "ArrowDown";

export const DROPDOWN_MENU_ARROW_UP_KEY = "ArrowUp";

export const DROPDOWN_MENU_SPACE_KEY = " ";

export const DROPDOWN_MENU_FOCUS_OPTIONS = {
    preventScroll: true
};

const DROPDOWN_MENU_SHORTCUT_KEYS: Record<string, Record<KbdPlatform, {
    ariaKey: string;
    label: string;
}>> = {
    backspace: {
        apple: {
            ariaKey: "Backspace",
            label: "⌫"
        },
        other: {
            ariaKey: "Backspace",
            label: "Backspace"
        }
    },
    delete: {
        apple: {
            ariaKey: "Delete",
            label: "⌦"
        },
        other: {
            ariaKey: "Delete",
            label: "Del"
        }
    },
    tab: {
        apple: {
            ariaKey: "Tab",
            label: "⇥"
        },
        other: {
            ariaKey: "Tab",
            label: "Tab"
        }
    }
};

const DROPDOWN_MENU_ARIA_KEYS: Record<string, Record<KbdPlatform, string>> = {
    control: {
        apple: "Control",
        other: "Control"
    },
    ctrl: {
        apple: "Control",
        other: "Control"
    },
    mod: {
        other: "Control",
        apple: "Meta"
    },
    esc: {
        apple: "Escape",
        other: "Escape"
    },
    escape: {
        apple: "Escape",
        other: "Escape"
    },
    enter: {
        apple: "Enter",
        other: "Enter"
    },
    return: {
        apple: "Enter",
        other: "Enter"
    },
    shift: {
        apple: "Shift",
        other: "Shift"
    },
    alt: {
        apple: "Alt",
        other: "Alt"
    },
    option: {
        apple: "Alt",
        other: "Alt"
    }
};

export const getDropdownMenuElement = (node: unknown) => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

export const getDropdownMenuItems = ({
    surface
}: {
    surface: HTMLElement;
}): Array<HTMLElement> => {
    return Array.from(surface.querySelectorAll<HTMLElement>(DROPDOWN_MENU_ITEM_SELECTOR));
};

export const getDropdownMenuActiveElement = () => {
    if(Platform.OS !== "web" || typeof document === "undefined") {
        return null;
    }

    return getDropdownMenuElement(document.activeElement);
};

export const getIsDropdownMenuKeyboardFocus = (node: unknown) => {
    const element = getDropdownMenuElement(node);

    if(!element) {
        return false;
    }

    try {
        return element.matches(":focus-visible");
    } catch {
        return false;
    }
};

export const getIsDropdownMenuEditable = (target: EventTarget | null) => {
    const element = getDropdownMenuElement(target);

    if(!element) {
        return false;
    }

    return element.isContentEditable || element.tagName === "INPUT" || element.tagName === "TEXTAREA";
};

export const getDropdownMenuSelectionText = () => {
    if(Platform.OS !== "web" || typeof globalThis.getSelection !== "function") {
        return "";
    }

    return globalThis.getSelection()?.toString() ?? "";
};

export const focusDropdownMenuElement = (node: unknown) => {
    getDropdownMenuElement(node)?.focus(DROPDOWN_MENU_FOCUS_OPTIONS);
};

export const normalizeDropdownMenuText = ({
    locale,
    text
}: {
    locale: string;
    text: string;
}) => {
    try {
        return text.trim().toLocaleLowerCase(locale);
    } catch {
        return text.trim().toLowerCase();
    }
};

export const getDropdownMenuNextIndex = ({
    currentIndex,
    length,
    key
}: {
    currentIndex: number;
    length: number;
    key: string;
}): number => {
    if(!length) {
        return -1;
    }

    if(key === DROPDOWN_MENU_ARROW_DOWN_KEY) {
        return (currentIndex + 1) % length;
    }

    if(key === DROPDOWN_MENU_ARROW_UP_KEY) {
        return currentIndex <= 0 ? length - 1 : currentIndex - 1;
    }

    if(key === "Home") {
        return 0;
    }

    if(key === "End") {
        return length - 1;
    }

    return -1;
};

export const getDropdownMenuTypeaheadIndex = ({
    currentIndex,
    textValues,
    buffer
}: DropdownMenuTypeaheadConstantType): number => {
    if(!textValues.length || !buffer) {
        return -1;
    }

    const chars = Array.from(buffer);

    const [
        firstChar
    ] = chars;

    const isRepeated = chars.length > 1 && chars.every((char) => char === firstChar);

    const search = isRepeated && firstChar ? firstChar : buffer;

    const start = Array.from(search).length === 1 ? currentIndex + 1 : Math.max(currentIndex, 0);

    for(let offset = 0; offset < textValues.length; offset += 1) {
        const index = (start + offset) % textValues.length;

        if(textValues[index]?.startsWith(search)) {
            return index;
        }
    }

    return -1;
};

export const getDropdownMenuShortcut = ({
    localize,
    platform,
    keys
}: DropdownMenuShortcutConstantType): DropdownMenuShortcutResultType => {
    const parsedKeys = parseKbdKeys({
        keys
    });

    const labels = parsedKeys.map((key, index) => {
        const extraKey = DROPDOWN_MENU_SHORTCUT_KEYS[key.trim().toLowerCase()];

        if(extraKey) {
            return extraKey[platform].label;
        }

        return resolveKbdKey({
            localize,
            platform,
            index,
            key
        }).label;
    });

    const ariaKeys = parsedKeys.map((key) => {
        const lowerKey = key.trim().toLowerCase();

        const extraKey = DROPDOWN_MENU_SHORTCUT_KEYS[lowerKey];

        if(extraKey) {
            return extraKey[platform].ariaKey;
        }

        const ariaKey = DROPDOWN_MENU_ARIA_KEYS[lowerKey];

        if(ariaKey) {
            return ariaKey[platform];
        }

        if(key === ",") {
            return "Comma";
        }

        return Array.from(key).length === 1 ? key.toUpperCase() : key;
    });

    return {
        text: labels.join(platform === "apple" ? "" : "+"),
        ariaShortcut: ariaKeys.join("+"),
        labels
    };
};
